/** Тексты тура (10 шагов), гида редактора (4 шага), чек-листа и подписей.
 * Формы интерфейсов и длины массивов фиксированы; тексты правит WI-12. Источник — прототип behaviour.md §3.1, §4. */

export interface TourStep {
  key: string;
  path: string;
  search?: string;
  tab?: 0 | 1 | 2;
  title: string;
  text: string;
  cta?: string;
  /** Значение data-tour или 'flow' (= .mk-flow). */
  spotlight?: string;
  requires?: 'manage' | 'billing';
  /** Показывается без права; навигации нет. */
  lockedTitle?: string;
  lockedText?: string;
  /** Только шаг 10. */
  secondary?: { label: string; to: string; requires?: 'manage' };
}

const LOCKED_MANAGE =
  'Создавать кампании может пользователь с правом управления кампаниями. Попросите администратора выдать доступ — а пока посмотрите, как читать результаты.';
const LOCKED_BILLING =
  'Баланс видит пользователь с финансовыми правами. Платные сообщения списываются по мере отправки, запас на счёте защищает цепочки от остановки.';

const EDITOR_SEARCH = '?template=first-to-second&tour=1';

export const TOUR_STEPS: readonly TourStep[] = [
  {
    key: 'welcome',
    path: '/campaign-dashboard',
    title: 'Добро пожаловать в БРАСОЛ Маркетинг',
    text: 'Это кабинет удержания гостей: кампании, цепочки сообщений, бюджет и результат. За 10 шагов покажем, как всё устроено. Ничего не отправится без вашего подтверждения.',
    cta: 'Начать',
  },
  {
    key: 'dashboard',
    path: '/campaign-dashboard',
    spotlight: 'dashboard-kpi',
    title: 'Результаты кампаний — что принесли сообщения',
    text: 'Здесь доставка, очередь, заказы после сообщений и выручка к расходам. Период и окно ожидания заказа меняют только расчёт отчёта.',
  },
  {
    key: 'campaigns',
    path: '/campaigns',
    spotlight: 'campaigns-tabs',
    title: 'Рекламные кампании',
    text: 'Все сценарии в одном списке: действующие, черновики, завершённые. Из строки можно открыть настройки, результат или сделать копию.',
  },
  {
    key: 'catalog',
    path: '/campaigns/new',
    spotlight: 'catalog-recommended',
    requires: 'manage',
    lockedTitle: 'Готовые задачи',
    lockedText: LOCKED_MANAGE,
    title: 'Готовые задачи',
    text: 'Новая кампания начинается с задачи: «Второй заказ», «Спящие гости», «День рождения». Условия и цепочка уже заполнены — остаётся проверить.',
  },
  {
    key: 'editor-audience',
    path: '/campaigns/new',
    search: EDITOR_SEARCH,
    tab: 0,
    spotlight: 'audience',
    requires: 'manage',
    lockedTitle: 'Шаг 1 — Кому отправить',
    lockedText: LOCKED_MANAGE,
    title: 'Шаг 1 — Кому отправить',
    text: 'Юрлицо, города и условия аудитории. Справа сразу видно, сколько гостей подходит и сколько это будет стоить.',
  },
  {
    key: 'editor-flow',
    path: '/campaigns/new',
    search: EDITOR_SEARCH,
    tab: 1,
    spotlight: 'flow',
    requires: 'manage',
    lockedTitle: 'Шаг 2 — Сценарий и сообщения',
    lockedText: LOCKED_MANAGE,
    title: 'Шаг 2 — Сценарий и сообщения',
    text: 'Сначала бесплатный пуш, потом письмо, потом SMS — только тем, кто не заказал. Нажмите на узел — справа откроются его текст, промокод и ожидание.',
  },
  {
    key: 'editor-launch',
    path: '/campaigns/new',
    search: EDITOR_SEARCH,
    tab: 2,
    spotlight: 'launch',
    requires: 'manage',
    lockedTitle: 'Шаг 3 — Проверка и запуск',
    lockedText: LOCKED_MANAGE,
    title: 'Шаг 3 — Проверка и запуск',
    text: 'Лимит бюджета, контрольная группа, время отправки. Кнопка «Подготовить запуск» покажет итог и попросит подтверждение.',
  },
  {
    key: 'billing',
    path: '/billing',
    spotlight: 'billing-balance',
    requires: 'billing',
    lockedTitle: 'Баланс и счета',
    lockedText: LOCKED_BILLING,
    title: 'Баланс и счета',
    text: 'Платные сообщения списываются с баланса по мере отправки. Пополнение — по счёту, запас на счёте защищает от остановки цепочек.',
  },
  {
    key: 'done',
    path: '/campaign-dashboard',
    title: 'Готово!',
    text: 'Обучение можно пройти снова из меню слева. Начните с задачи «Второй заказ» — это самый быстрый способ вернуть гостей.',
    cta: 'Завершить',
    secondary: {
      label: 'Начать с задачи «Второй заказ»',
      to: '/campaigns/new?template=first-to-second&guide=1',
      requires: 'manage',
    },
  },
];

export interface GuideStep {
  title: string;
  text: string;
  /** Что именно нажать в режиме практики. */
  practiceHint?: string;
  /** Текст шага 4 без права запуска (используется и туром на шаге 8). */
  noLaunchText?: string;
}

export const GUIDE_STEPS: readonly [GuideStep, GuideStep, GuideStep] = [
  {
    title: '1. Выберите свою аудиторию',
    text: 'Укажите юридическое лицо и города один раз. Условия уже заполнены под задачу — их можно изменить. Справа сразу видно, сколько гостей подходит.',
    practiceHint:
      'Выберите юридическое лицо и хотя бы один город в подсвеченном блоке. Условия второго заказа уже подготовлены. Затем нажмите «Сделал — дальше».',
  },
  {
    title: '2. Соберите сценарий и тексты',
    text: 'Пуши бесплатные; для email и SMS нужен баланс. Нажмите на узел — справа откроются его текст, промокод, ожидание и условие перехода. Подстановки {name}, {dish} и {promo} заменяются для каждого гостя.',
    practiceHint:
      'Откройте каждое сообщение в сценарии: выберите шаблон или напишите текст, проверьте промокод и ссылку, задайте ожидание между сообщениями. Ненужный узел можно удалить крестиком.',
  },
  {
    title: '3. Проверьте и запустите',
    text: 'Укажите лимит или включите «Без лимита». Сначала отправьте тест на свой номер — блок «Тестовое сообщение» ниже. Нажмите «Подготовить запуск» — покажем итог и попросим подтверждение.',
    practiceHint:
      'Проверьте лимит и время отправки, затем отправьте тест на свой номер в блоке «Тестовое сообщение». Черновик сохраняется автоматически, отправка не начнётся без подтверждения.',
    noLaunchText:
      'Лимит бюджета, контрольная группа, время отправки. Запуск подтверждает пользователь с правом запуска — вы сможете подготовить и сохранить черновик.',
  },
];

export const GUIDE_LABELS = {
  next: 'Понятно, дальше',
  last: 'Понятно',
  hide: 'Скрыть подсказки',
  practiceNext: 'Сделал — дальше',
  practiceFinish: 'Завершить обучение',
  practiceLaunch: 'Подготовить запуск',
  practiceFinishNoLaunch: 'Завершить без запуска',
  saving: 'Сохраняем…',
} as const;

export const CHECKLIST_TEXT = {
  kicker: 'ПЕРВЫЕ ШАГИ',
  title: 'Настройте первую кампанию за 4 шага',
  text: 'Обучение проведёт по аудитории, цепочке каналов, предложению и запуску. Ничего не отправится без подтверждения.',
  progress: (done: number, total: number) => `Выполнено ${done} из ${total}`,
  start: 'Начать обучение',
  catalog: 'Выбрать задачу',
  hide: 'Скрыть',
  show: 'Показать чек-лист',
} as const;

export const TOUR_LABELS = {
  kicker: (n: number, total: number) => `ОБУЧЕНИЕ · ${n} ИЗ ${total}`,
  resumeKicker: 'ОБУЧЕНИЕ · ПРОДОЛЖИТЬ',
  resumeTitle: 'Продолжить обучение?',
  resumeText: (n: number, title: string) => `Вы остановились на шаге ${n} — «${title}».`,
  skip: 'Пропустить',
  skipAria: 'Пропустить обучение',
  back: 'Назад',
  next: 'Далее',
  start: 'Начать',
  finish: 'Завершить',
  resume: 'Продолжить',
  restart: 'Начать сначала',
} as const;
